import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, Switch, StyleSheet, Dimensions, Alert } from 'react-native';
import moment from 'moment';
import { PlusIcon, MagnifyingGlassIcon, PenIcon } from '@phosphor-icons/react';
import { observer } from 'mobx-react';
import { userStore } from '../stores/UserStore';
import { adminAccessStore } from '../stores/AdminAccessStore';
import { navigate } from '../services/NavigationService';
import { EnumRoutesAppWeb } from '../enum/EnumRoutesAppWeb';
import { Theme } from '../enum/Theme';
import { multiSortArray } from '@utils/sortUtils';
import SpaceW from '@comps/SpaceW';
import { User } from '@interfaces/iuser';
import { toJS } from 'mobx';
import ModalDate from '../comps/ModalDate';

const Users = observer(() => {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [visible, setVisible] = useState(false);
  const [userEdit, setUserEdit] = useState<User | undefined>(undefined);

  const { loading, users } = userStore;
  const { hasAdminAccess, toggleAdminAccess } = adminAccessStore;

  useEffect(() => {
    const screenHeight = Dimensions.get('window').height;
    const itemHeight = 60;
    const maxItems = Math.floor((screenHeight - 200) / itemHeight);
    setItemsPerPage(maxItems);
    
    // Inicializa o monitoramento do acesso admin
    adminAccessStore.init();
    
    // Cleanup ao desmontar o componente
    return () => {
      adminAccessStore.dispose();
    };
  }, []);

  const filteredNormais = users.filter((item) => {
    const searchLower = search.toLowerCase();
    return (
      item.nome?.toLowerCase().includes(searchLower) ||
      item.email?.toLowerCase().includes(searchLower)
    );
  });

  multiSortArray(filteredNormais, ['nome'], ['ASC'])

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = filteredNormais.slice(startIndex, endIndex);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const openModal = (user: User) => {
    setUserEdit(toJS(user));
    setVisible(true);
  };

  const closeModal = () => {
    setUserEdit(undefined);
    setVisible(false);
  };

  const handleSaveUserEdit = (user: User) => {
    userStore.updateUserData(user);
    closeModal();
  };

  const handleToggleAdminAccess = async (userUid: string, userName: string) => {
    try {
      const success = await toggleAdminAccess(userUid);
      if (success) {
        const currentAccess = hasAdminAccess(userUid);
        Alert.alert(
          'Acesso Admin Alterado',
          `${userName} ${currentAccess ? 'agora tem' : 'não tem mais'} acesso ao painel administrativo.`
        );
      } else {
        Alert.alert('Erro', 'Não foi possível alterar o acesso admin. Tente novamente.');
      }
    } catch (error) {
      console.error('Erro ao alterar acesso admin:', error);
      Alert.alert('Erro', 'Ocorreu um erro ao alterar o acesso admin.');
    }
  };

  if (loading) {
    return <Text style={{ textAlign: 'center', marginTop: 50 }}>Carregando...</Text>;
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity style={styles.btnCadastrar}>
          <PlusIcon size={20} color="#fff" />
          <Text style={styles.btnCadastrarText}>Cadastrar</Text>
        </TouchableOpacity>
        <View style={styles.searchContainer}>
          <MagnifyingGlassIcon size={20} color="#ccc" />
          <TextInput
            placeholder="Pesquise por nome ou e-mail..."
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
          />
        </View>
      </View>

      <View style={styles.tableHeader}>
        <Text style={styles.headerText}>Nome</Text>
        <Text style={styles.headerText}>E-mail</Text>
        <Text style={styles.headerText}>Acesso Admin</Text>
        <Text style={styles.headerText}>Acesso grátis até</Text>
      </View>

      <ScrollView>
        {currentPageItems.map((item, index) => (
          <View style={styles.row} key={item.uid || index}>
            <TouchableOpacity style={{ flex: 1 }} onPress={() => navigate(EnumRoutesAppWeb.showUser, { user: item })}>
              <Text style={styles.rowText}>{item.nome}</Text>
            </TouchableOpacity>
            <Text style={styles.rowText}>{item.email}</Text>
            <View style={styles.iconRow}>
              <Switch 
                value={hasAdminAccess(item.uid)} 
                onValueChange={() => handleToggleAdminAccess(item.uid, item.nome || 'Usuário')}
              />
            </View>
            <View style={styles.actionCell}>
              <Text>
                {item.endFreeAccessDate ? moment(item.endFreeAccessDate).format('DD/MM/YYYY') : '-'}
              </Text>
              <SpaceW />
              <TouchableOpacity style={{ flex: 1 }} onPress={() => openModal(item)}>
                <PenIcon />
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>

      <ModalDate
        visible={visible}
        onClose={closeModal}
        user={userEdit}
        onSave={handleSaveUserEdit}
      />

      <View style={styles.pagination}>
        {Array.from({ length: Math.ceil(filteredNormais.length / itemsPerPage) }, (_, index) => (
          <TouchableOpacity
            key={index}
            style={[styles.page, page === index + 1 && styles.activePage]}
            onPress={() => setPage(index + 1)}
          >
            <Text style={{ color: page === index + 1 ? '#fff' : Theme.foreground }}>{index + 1}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: { flex: 1, padding: 20, backgroundColor: '#F2F2F2' },
  header: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 },
  headerText: { flex: 1, fontWeight: 'bold', backgroundColor: Theme.foreground, color: '#fff', padding: 10 },
  tableHeader: { flexDirection: 'row', backgroundColor: Theme.foreground },
  row: { flexDirection: 'row', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#ccc' },
  rowText: { flex: 1 },
  iconRow: { flex: 1 },
  actionCell: { flex: 1, justifyContent: 'flex-start', flexDirection: 'row' },
  btnCadastrar: { backgroundColor: Theme.foreground, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 5, flexDirection: 'row', alignItems: 'center' },
  btnCadastrarText: { color: '#fff', marginLeft: 5 },
  searchContainer: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#ccc', borderRadius: 5, paddingHorizontal: 8, flex: 1, marginLeft: 10 },
  searchInput: { flex: 1, padding: 8 },
  pagination: { flexDirection: 'row', justifyContent: 'center', marginTop: 20 },
  page: { marginHorizontal: 5, padding: 10, borderRadius: 5, backgroundColor: '#eee' },
  activePage: { backgroundColor: Theme.foreground },
});

export default Users;
